// ABOUTME: Modal that diagnoses why a shipper cost rule of a given priority does/doesn't link to an order.
// ABOUTME: Sends orderId + 1-based priority to /invoices/orders/checkcostrule and renders a per-condition checklist.

import { Box, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import { tokens } from "../../../theme";
import SHModal from "../../theme/Modal/SHModal";
import Subheading from "../../theme/text/Subheading";
import Text from "../../theme/text/Text";
import Shbutton from "../../theme/buttons/Shbutton";
import ButtonGroup from "../../theme/buttons/ButtonGroup";
import NumberInput from "../../theme/inputs/NumberInput";
import { useCheckOrderCostRuleMutation } from "../../../newapi/financial/invoiceSlice";
import { showToastMessageRequestError } from "../../../utils/toasts";

const MCheckCostRule = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t } = useTranslation();

    const [priority, setPriority] = useState(1);
    const [result, setResult] = useState(null);

    const [checkOrderCostRule, { isLoading }] = useCheckOrderCostRuleMutation();

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    // Reset the input and any previous diagnosis whenever the modal (re)opens.
    useEffect(() => {
        if (props.open) {
            setPriority(1);
            setResult(null);
        }
    }, [props.open]);

    const orderId = props.orderData?.id;

    const runCheck = async () => {
        if (!orderId) {
            return;
        }

        try {
            const data = await checkOrderCostRule({ orderId, priority }).unwrap();
            setResult(data);
        } catch (err) {
            setResult(null);
            showToastMessageRequestError(t("check_cost_rule_failed"), t, err);
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !isLoading) {
            runCheck();
        }
    };

    const selectedStep = result?.selectedStep;
    const matchedStep = result?.matchedStep;
    // matchedStep is 1-based (same convention the user types), so compare it to the requested priority.
    const requestedRuleWins = matchedStep !== null && matchedStep !== undefined && matchedStep === priority;

    return (
        <SHModal
            open={props.open}
            onClose={props.handleClose}
            onKeyDown={handleKeyDown}
        >
            <Box sx={{ maxWidth: 500 }}>
                <Box
                    sx={{
                        textAlign: "center",
                        paddingY: 4,
                        paddingX: 5,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                    }}
                >
                    <Subheading>{t("check_cost_rule")}</Subheading>
                    <Text center light>{t("check_cost_rule_subtitle")}</Text>
                </Box>

                <Box
                    sx={{
                        background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                        padding: 3,
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <Box>
                        <Text light sx={{mr: 1}}>{t("order")}</Text>
                        <Text bold>{props.orderData?.barcode ?? orderId}</Text>
                    </Box>

                    <Box>
                        <Text light>{t("priority")}</Text>
                        <Box
                            sx={{
                                flex: 1,
                                backgroundColor: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                                borderRadius: 4,
                                display: "flex",
                                height: 32,
                                mt: 0.5,
                            }}
                        >
                            <NumberInput
                                btnSize={"24"}
                                autoFocus={true}
                                min={1}
                                onlyPositive
                                value={priority}
                                change={(value) => setPriority(value === '' ? '' : value)}
                            />
                        </Box>
                    </Box>

                    {selectedStep &&
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                                pt: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    borderRadius: 4,
                                    padding: 2,
                                    background: selectedStep.isMatch
                                        ? colors.green[100]
                                        : colors.red[100],
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                {selectedStep.isMatch
                                    ? <CheckCircleOutlineIcon sx={{ color: colors.green[400] }} />
                                    : <HighlightOffIcon sx={{ color: colors.red[400] }} />
                                }
                                <Text bold>
                                    {selectedStep.isMatch
                                        ? t("cost_rule_matches")
                                        : t("cost_rule_does_not_match")}
                                </Text>
                            </Box>

                            {selectedStep.conditions?.map((condition, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        borderRadius: 4,
                                        padding: 1.5,
                                        background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                                    }}
                                >
                                    {condition.match
                                        ? <CheckCircleOutlineIcon fontSize="small" sx={{ color: colors.green[400] }} />
                                        : <HighlightOffIcon fontSize="small" sx={{ color: colors.red[400] }} />
                                    }
                                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                                        <Text bold>{condition.key}</Text>
                                        <Text light>
                                            {condition.nominator} {formatConditionValue(condition.value)}
                                        </Text>
                                    </Box>
                                </Box>
                            ))}

                            <Box sx={{ pt: 0.5 }}>
                                {matchedStep === null || matchedStep === undefined
                                    ? <Text light>{t("cost_rule_none_matches")}</Text>
                                    : requestedRuleWins
                                        ? <Text>{t("cost_rule_this_rule_wins")}</Text>
                                        : <Text>{t("cost_rule_other_wins", { priority: matchedStep })}</Text>
                                }
                            </Box>
                        </Box>
                    }
                </Box>

                <Box
                    component="footer"
                    sx={{
                        width: 1,
                        paddingBottom: isStandAlone ? "32px" : "16px",
                        display: "flex",
                        alignItems: "center",
                        paddingRight: 3,
                        zIndex: 1058,
                        position: "relative",
                        paddingTop: 2,
                        background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                        borderBottomLeftRadius: 24,
                        borderBottomRightRadius: 24,
                        justifyContent: "space-between",
                        "@media screen and (max-width: 47.9375em)": {
                            paddingRight: 0,
                        }
                    }}
                >
                    <Shbutton isCancel={true} className={"always-visible"} onClick={props.handleClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon />}>{t("cancel")}</Shbutton>
                    <ButtonGroup version={2}>
                        <Shbutton
                            className={"group-btn"}
                            color={theme.palette.mode === "dark" ? "blue" : "primary"}
                            onClick={runCheck}
                            variant="contained"
                            disabled={isLoading || !orderId || priority === '' || priority < 1}
                        >
                            {t("check")}
                        </Shbutton>
                    </ButtonGroup>
                </Box>
            </Box>
        </SHModal>
    );
};

// Renders the configured condition target value; arrays are joined so a multi-value
// condition (e.g. a list of countries) reads as a single line.
const formatConditionValue = (value) => {
    if (Array.isArray(value)) {
        return value.join(", ");
    }
    return value;
};

export default MCheckCostRule;
